import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { API_URL } from "../utils/constants.js";
import { DEFAULT_IMAGE } from "../utils/constants.js";
import { HeroEditable } from "../components/heroSection.jsx";
import Section from "../components/base/Section.jsx";
import DateRangePicker from "../components/dateRangePicker.jsx";
import Label from "../components/base/Label.jsx";
import Input from "../components/base/Input.jsx";
import Button from "../components/base/Button.jsx";
import { theme } from "../theme.js";
import DateRangeInput from "../components/DateRangeInput.jsx";
import LoadingOverlay from "../components/LoadingOverlay.jsx";


export default function CreateEvent() {
  const navigate = useNavigate();
  const loc = useLocation();
  const editEvent = loc.state?.editEvent;
  const orgId = JSON.parse(localStorage.getItem("user")).org_id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "Nouvel Evénement",
    location: "",
    country:"",
    startDate: "",
    endDate: "",
    description: "",
    website: "",
    organizer_id: orgId,
    image: DEFAULT_IMAGE,
  });

  // Pre-fill form if editing an event
  useEffect(() => {
    if (editEvent) {
      const formatDateString = (dateString) => {
        if (!dateString) return "";
        // Parse the date and return in YYYY-MM-DD format without timezone conversion
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      setFormData({
        title: editEvent.name || "Nouvel Evénement",
        location: editEvent.location || "",
        country: editEvent.country || "",
        startDate: formatDateString(editEvent.start_date),
        endDate: formatDateString(editEvent.end_date),
        description: editEvent.description || "",
        website: editEvent.website || "",
        organizer_id: orgId,
        image: editEvent.image || DEFAULT_IMAGE,
      });
    }
  }, [editEvent]);

  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
    console.log('Selected image file:', formData);
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    const endpoint = editEvent ? `${API_URL}/api/events/${editEvent.id_bdd}` : `${API_URL}/api/events`;
    const method = editEvent ? "PUT" : "POST";
    
    const response = await fetch(endpoint, {
      method: method,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    console.log(formData);

    if (!response.ok) {
      throw new Error("Error saving event");
    }

    const data = await response.json();
    console.log("Event saved:", data);
    
    // Navigate to the event page after successful save/edit
    const eventId = editEvent ? editEvent.id_bdd : data.id_bdd;
    navigate(`/events/${eventId}`, { state: { event: data } });

  } catch (err) {
    console.error("Backend error:", err);
  }
};

const getValidationErrors = () => {
  const errors = [];

  // Check basic fields
  if (!formData.title || formData.title.trim() === "") {
    errors.push("Le titre est requis");
  }
  if (!formData.location || formData.location.trim() === "") {
    errors.push("Le lieu est requis");
  }
  if (!formData.country || formData.country.trim() === "") {
    errors.push("Le pays est requis");
  }
  if (!formData.description || formData.description.trim() === "") {
    errors.push("La description est requise");
  }
  if (!formData.website || formData.website.trim() === "") {
    errors.push("Le site web est requis");
  }

  // Check dates exist
  if (!formData.startDate || formData.startDate.trim() === "") {
    errors.push("La date de début est requise");
  }
  if (!formData.endDate || formData.endDate.trim() === "") {
    errors.push("La date de fin est requise");
  }

  // Check date range validations only if both dates exist
  if (formData.startDate && formData.endDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);

    // Start date must be today or in the future
    if (startDate < today) {
      errors.push("La date de début doit être aujourd'hui ou dans le futur");
    }

    // Start date must be before end date
    if (startDate > endDate) {
      errors.push("La date de début doit être avant la date de fin");
    }
  }

  return errors;
};

const isFormValid = () => {
  return getValidationErrors().length === 0;
};

const validationErrors = getValidationErrors();

if (loading) {
    return <LoadingOverlay isVisible={true} message={editEvent ? "Mise à jour de l'événement..." : "Création de l'événement..."} />;
  }

return (
    <div>
      <HeroEditable formData={formData} handleChange={handleInputChange} onImageChange={handleImageChange} />
      {/* Main Content */}
      <div style={styles.content}>
        <div style={styles.mainColumn}>
          <Section title="Informations" border={false} style={{paddingBottom: 0}}>
              <DateRangeInput
                label="Dates de l'événement"
                startName= "startDate"
                endName= "endDate"
                startDate={formData.startDate}
                endDate={formData.endDate}
                setFormData={setFormData}
                required
              />
              <Input 
              name="description" 
              label="Description" 
              value={formData.description} 
              setFormData={setFormData} 
              placeholder="Description" 
              multiline={true} 
              required/>
              <Input 
              name="website" 
              label="Site Web" 
              value={formData.website} 
              setFormData={setFormData} 
              placeholder="www.exemple.fr" 
              required/>
              <div style={{ display: "flex", gap: "20px", width: "100%" }}>
                <Input 
                name="location" 
                label="Lieu" 
                value={formData.location} 
                setFormData={setFormData} 
                placeholder="Lieu"
                required/>
                <Input 
                name="country" 
                label="Pays"
                value={formData.country}
                setFormData={setFormData} 
                placeholder="Pays"
                required/>
            </div>
          </Section>
          {validationErrors.length > 0 && (
            <div style={styles.errorContainer}>
              {validationErrors.map((error, index) => (
                <p key={index} style={styles.errorText}>• {error}</p>
              ))}
            </div>
          )}
          <div style={styles.buttonContainer}>
            <Button title={editEvent ? "Modifier l'événement" : "Créer l'événement"} onClick={handleSubmit} disabled={!isFormValid()} />
            {editEvent && <Button title="Annuler" onClick={() => navigate(-1)} style="cancel" />}
          </div>

        </div>
      </div>
    </div>
  );
}

const styles = {
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
    gap: "40px",
    alignItems: "center",
  },
  mainColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xxl,

  },
  datePickerWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  dateDisplayButton: {
    padding: "10px 12px",
    borderRadius: "6px",
    border: `1px solid ${theme.colors.border}`,
    fontSize: "14px",
    backgroundColor: "#fff",
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.2s",
  },
  pickerContainer: {
    position: "absolute",
    zIndex: 10,
    top: "100%",
    left: 0,
    marginTop: "8px",
    background: "#fff",
    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
    borderRadius: "6px",
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  errorContainer: {
    backgroundColor: theme.colors.errorBg,
    border: `1px solid ${theme.colors.error}`,
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    marginTop: theme.spacing.sm,
  },
  errorText: {
    color: theme.colors.error,
    fontSize: theme.fonts.size.md,
    margin: `${theme.spacing.xs} 0`,
    fontWeight: theme.fonts.weight.medium,
  },
};